import { Component, type ReactNode, type ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorPage } from './ErrorPage';
import { isChunkLoadError, handleChunkLoadError } from '@/utils/chunkErrorHandler';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 *
 * IMPORTANT: Error logging does NOT include any PII - only error messages,
 * component stack traces, and timestamps are logged.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error without PII - only technical details
    // DO NOT log user data, emails, account numbers, etc.
    const errorLog = {
      timestamp: new Date().toISOString(),
      errorName: error.name,
      errorMessage: error.message,
      componentStack: errorInfo.componentStack,
      url: window.location.pathname, // Only path, not full URL with potential PII in params
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', errorLog);
    }

    // Send to Sentry in production (Sentry handles missing/invalid DSN gracefully)
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        url: window.location.pathname,
      },
      tags: isChunkLoadError(error) ? { type: 'chunk_load_error' } : undefined,
    });

    // WP-BUG-52: If this is a chunk load error, attempt a single hard reload.
    // handleChunkLoadError returns true if reload was triggered (first attempt),
    // or false if retries are exhausted (flag already set in sessionStorage).
    // When reload is triggered, the page navigates away so no further action needed.
    if (isChunkLoadError(error)) {
      const reloadTriggered = handleChunkLoadError(error);
      if (reloadTriggered) {
        // Reload initiated -- page will navigate away.
        // Return early to avoid calling onError for a transient issue.
        return;
      }
      // Retries exhausted -- fall through to show chunk-specific error UI
    }

    // Call optional error handler (e.g., for sending to error tracking service)
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, isChunkError: false });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // WP-BUG-52: Show chunk-specific error message when auto-reload retries
      // are exhausted. This happens when the app was updated and cached JS
      // chunks are no longer available on the server.
      if (this.state.isChunkError) {
        return (
          <ErrorPage
            code={500}
            title="App Updated"
            message="A new version of FlowIO is available. Please reload the page to continue."
            showReload
            onReload={this.handleReload}
            showDashboardLink={false}
          />
        );
      }

      // Render default error page
      return (
        <ErrorPage
          code={500}
          title="Something went wrong"
          message="An unexpected error occurred. Please try again or return to the home page."
          showRetry
          onRetry={this.handleReset}
          showReload
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
